import {
  MeResponseV1,
  MySettingsResponseV1,
  OPERATOR_PERMISSIONS,
  PLATFORM_PERMISSIONS,
  type OperatorPermission,
  type PlatformPermission,
  UpdateMySettingsRequestV1,
} from "@strust/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useApiClient } from "../../../shared/api/api-provider.tsx";
import { defineEndpoint } from "../../../shared/api/endpoint.ts";

const isKnownPlatformPermission = (code: string): code is PlatformPermission =>
  (PLATFORM_PERMISSIONS as readonly string[]).includes(code);
const isKnownOperatorPermission = (code: string): code is OperatorPermission =>
  (OPERATOR_PERMISSIONS as readonly string[]).includes(code);
const KnownCodes = <T extends string>(isKnown: (code: string) => code is T) =>
  z.array(z.string().max(64)).transform((codes) => codes.filter(isKnown));

/**
 * `/me` as an open console reads it (review of pull request 282): a platform or operator permission
 * this build does not know yet (a deploy added one while the tab stayed open) is dropped instead of
 * failing the whole answer, so the console keeps working with the codes it knows.
 */
export const TolerantMeResponseV1 = MeResponseV1.extend({
  operators: z.array(
    MeResponseV1.shape.operators.element.extend({ permissions: KnownCodes(isKnownOperatorPermission) }),
  ),
  platform_permissions: KnownCodes(isKnownPlatformPermission),
});

export const meEndpoint = defineEndpoint({ method: "GET", path: "/me", response: TolerantMeResponseV1 });

export const updateSettingsEndpoint = defineEndpoint({
  method: "PUT",
  path: "/me/settings",
  body: UpdateMySettingsRequestV1,
  response: MySettingsResponseV1,
});

export const ME_QUERY_KEY = ["me"] as const;

/** The console user's access context and settings (locale, time zone). */
export function useMe() {
  const client = useApiClient();
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: ({ signal }) => client.call(meEndpoint, { signal }),
  });
}

/**
 * Saves locale, time zone and/or theme (`PUT /me/settings`) and merges the result into the `/me`
 * cache. Saves run one after another (one mutation scope): two quick changes (language, then
 * theme) never race on the same console user, which `iam` would refuse as a concurrent change.
 */
export function useUpdateSettings() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    scope: { id: "me-settings" },
    mutationFn: (body: UpdateMySettingsRequestV1) => client.call(updateSettingsEndpoint, { body }),
    onSuccess: (saved) => {
      queryClient.setQueryData<MeResponseV1>(ME_QUERY_KEY, (current) =>
        current
          ? { ...current, locale: saved.locale, time_zone: saved.time_zone, theme: saved.theme }
          : current,
      );
    },
  });
}
